package com.iregained.eventapp.theme

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color

/**
 * @property nearBackground A useful color to apply to a container that should differentiate itself from the background
 *   but just a little.
 */
class SitePalette(
    val nearBackground: Color,
    val lightGrey: Color,
    val cobweb: Color,
    val transparent: Color,
    val brand: Brand,
    val custom: Custom,
) {
    class Brand(
        // --- 10% ACCENT COLORS ---
        // Reserved strictly for high-value interactions (CTAs, Links, Icons, Active Tabs)
        val primary: Color = Color.rgb(0x008080),          // Core Teal
        val primaryHover: Color = Color.rgb(0x006666),     // Darker Teal for hover state
        val accent: Color = Color.rgb(0xF3DB5B),           // Accent Yellow / Gold
        val accentGreen: Color = Color.rgb(0x28A745),      // Confirmed / Available Green

        // --- 30% SECONDARY COLORS ---
        // Used for alternating section backgrounds (e.g., Hero sections, Testimonials)
        val primarySoft: Color = Color.rgb(0xE5F2F2),
    )

    class Custom(
        // Direct accessors for dynamic event sections
        val brandPrimary: Color = Color.rgb(0x008080),
        val accentYellow: Color = Color.rgb(0xF3DB5B),
        val accentGreen: Color = Color.rgb(0x28A745),

        // --- NEW: SEMANTIC / STATUS COLORS ---
        val error: Color = Color.rgb(0xDC3545),            // Red for login errors, blocked slots, cancelations
        val warning: Color = Color.rgb(0xFFC107),          // Orange/Yellow for warnings, paused events
        val info: Color = Color.rgb(0x17A2B8),             // Blue for informative text/toasts

        // Typography
        val darkText: Color = Color.rgb(0x323232),

        // Surface & Card Backgrounds
        val white: Color = Colors.White,
        val lightGray: Color = Color.rgb(0xF8F9FA),
        val gray: Color = Color.rgb(0xF0F1F2),
        val cardBackground: Color = Colors.White,
        val surfaceBackground: Color = Color.rgb(0xF8F9FA),

        // Interactive & Neutral UI Utility States
        val neutralLight: Color = Color.rgb(0xEDF2F7),     // Inactive tabs & light borders
        val neutralDisabled: Color = Color.rgb(0xE2E8F0),  // Full / blocked time slot pills
        val divider: Color = Color.rgb(0xE2E8F0)           // Structural dividers
    )
}

object SitePalettes {
    val light = SitePalette(
        nearBackground = Color.rgb(0xF4F6FA),
        cobweb = Colors.LightGray,
        lightGrey = Colors.LightGray,
        transparent = Colors.Transparent, // Initialized here
        brand = SitePalette.Brand(),
        custom = SitePalette.Custom()
    )
}

fun ColorMode.toSitePalette(): SitePalette {
    return SitePalettes.light
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    // TELL KOBWEB TO NEVER LISTEN TO THE OS DARK MODE PREFERENCE
    ctx.config.initialColorMode = ColorMode.LIGHT

    // Set the 60% and 30% global defaults at the absolute root level
    ctx.theme.palettes.light.background = Colors.White
    ctx.theme.palettes.light.color = Color.rgb(0x323232)

    ctx.theme.palettes.dark.background = Color.rgb(0x06080B)
    ctx.theme.palettes.dark.color = Colors.White
}
